import { ApiModel } from '@composable-api/api.model'
import type { Translation, Monetary } from '@sim-api-types/general-data'
import type { ShippingServiceType } from '@sim-api-enums/shipping'
import { ShippingMethodAddressRequirement } from '@sim-api-enums/shipping'
import type { GetPriceOptions } from '@simploshop-models/product.model'
import { getFormattedPrice, getPriceValue } from '../utils/localization'

interface Attributes {
    [RefundShippingMethodModel.ATTR_ID]: number
    [RefundShippingMethodModel.ATTR_NAME]: Translation<string>
    [RefundShippingMethodModel.ATTR_DESCRIPTION]: Translation<string> | null
    [RefundShippingMethodModel.ATTR_POSITION]: number
    [RefundShippingMethodModel.ATTR_PRICE]: Monetary
    [RefundShippingMethodModel.ATTR_TAXED_PRICE]: Monetary
    [RefundShippingMethodModel.ATTR_SHIPPING_SERVICE]: ShippingServiceType
    [RefundShippingMethodModel.ATTR_CAN_USE]: boolean
    [RefundShippingMethodModel.ATTR_HAS_OPTIONS]: boolean
    [RefundShippingMethodModel.ATTR_ADDRESS_REQUIREMENT]: ShippingMethodAddressRequirement
}

interface Embeds {
}

export class RefundShippingMethodModel extends ApiModel<Attributes, Embeds> {
    static key = 'RefundShippingMethodModel'

    static readonly ATTR_ID = 'id'
    static readonly ATTR_NAME = 'name'
    static readonly ATTR_DESCRIPTION = 'description'
    static readonly ATTR_POSITION = 'position'
    static readonly ATTR_PRICE = 'price'
    static readonly ATTR_TAXED_PRICE = 'taxed_price'
    static readonly ATTR_SHIPPING_SERVICE = 'shipping_service'
    static readonly ATTR_CAN_USE = 'can_use'
    static readonly ATTR_HAS_OPTIONS = 'has_options'
    static readonly ATTR_ADDRESS_REQUIREMENT = 'address_requirement'

    get id() {
        return this._getAttribute(RefundShippingMethodModel.ATTR_ID)
    }

    get name() {
        return this._getAttribute(RefundShippingMethodModel.ATTR_NAME)
    }

    get description() {
        return this._getAttribute(RefundShippingMethodModel.ATTR_DESCRIPTION)
    }

    get position() {
        return this._getAttribute(RefundShippingMethodModel.ATTR_POSITION)
    }

    get price() {
        return this._getAttribute(RefundShippingMethodModel.ATTR_PRICE)
    }

    get taxedPrice() {
        return this._getAttribute(RefundShippingMethodModel.ATTR_TAXED_PRICE)
    }

    get shippingService() {
        return this._getAttribute(RefundShippingMethodModel.ATTR_SHIPPING_SERVICE)
    }

    get canUse() {
        return this._getAttribute(RefundShippingMethodModel.ATTR_CAN_USE)
    }

    get hasOptions() {
        return this._getAttribute(RefundShippingMethodModel.ATTR_HAS_OPTIONS)
    }

    get addressRequirement() {
        return this._getAttribute(RefundShippingMethodModel.ATTR_ADDRESS_REQUIREMENT)
    }

    isFree() {
        return this.price ? getPriceValue(this.price) === 0 : false
    }

    /**
     * Get the price of the shipping method.
     * @param options
     */
    getPrice(options?: Partial<GetPriceOptions>) {
        return (options?.tax !== false ? this.taxedPrice : this.price) ?? null
    }

    getFormattedPrice(options?: Partial<GetPriceOptions>) {
        return getFormattedPrice(this.getPrice(options))
    }

    allowsDifferentBillingAddress() {
        return this.addressRequirement === ShippingMethodAddressRequirement.BOTH
    }
}
